import Button, { ButtonGroup } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import { ErrorMessage, Field } from '@atlaskit/form';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import InfoIcon from '@atlaskit/icon/glyph/info';
import Select, { ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Y200 } from '@atlaskit/theme/colors';
import { DismissableInlineMessage, entitlementUtils, InfoPanel, useFeatureToggle } from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import {
  BillingFrequency,
  EntitlementDetails,
  FetchLinkedEntitlementsForTableQuery,
  InvoiceGroup,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  Maybe,
  Offering,
  PricingPlan,
  PricingType,
  SyntheticPricingPlan,
} from '@css-xp/graphtypes/src/generated-types-new';
import { EntitlementBaseProduct, EntitlementDefaultsResponse } from '@atlassian/cssxp-types-entitlement';
import { OfferingName, PricingPlanType } from '@atlassian/cssxp-typescommon';
import {
  ENABLE_JCA_SWITCH_TO_ANNUAL,
  ENABLE_PRICING_PLAN_CHANGE,
  ENABLE_VIEW_PRICING_DETAILS_ON_MANAGE,
} from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { getUserTier, EntitlementEdition, getUserTierOptions } from '@atlassian/cssxp-utils-entitlement';
import classNames from 'classnames';
import { isInteger, isNil } from 'lodash';
import capitalize from 'lodash/capitalize';
import defaultTo from 'lodash/defaultTo';
import React, { useEffect, useRef, useState } from 'react';
import { isTrialEntitlement } from '../../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import { ChangePricePlan } from '../../../components/ChangePricePlan/src/change-price-plan';
import { FormData } from './manage-entitlement';
import './manage-entitlement.scss';

interface OfferingSelectItem {
  label: string;
  value: string;
}

interface UserTierSelectItem {
  label: string;
  value: string;
}

interface Props {
  entitlement: EntitlementDetails;
  formState: FormData;
  availablePricingPlans?: PricingPlan[];
  availableOfferings: Offering[];
  transactionAccountContext: TransactionAccountContextType;
  isSkipTrialDisabled: boolean;
  isSkipTrialChecked: boolean;
  onSkipTrialChange: (value: boolean) => void;
  onSkipTrialDisableChange: (value: boolean) => void;
  productSkipTrialEligible: boolean;
  setProductSkipTrialEligible: (value: boolean) => void;
  isImmediateFlag: boolean;
  onIsImmediateFlagChange: (value: boolean) => void;
  evaluatePricingPlan?: (value: string) => void;
  onSelectOfferingId?: (value?: string) => void;
  selectedOfferingId?: string;
  linkedEntsTableVisible?: boolean;
  linkedEntitlementsData?: FetchLinkedEntitlementsForTableQuery;
  linkedEntitlementsLoading?: boolean;
  linkedEntitlementsError?: Error;
  productType?: Maybe<LinkedProductTypes>;
  parentContainerID?: string;
  setBillingFrequency: (value: BillingFrequency) => void;
  setUserTier: (value: string | undefined) => void;
  billingFrequency: BillingFrequency;
  isBillingCycleChanged?: boolean;
  defaultEntitlementData?: EntitlementDefaultsResponse;
  isDefaultEntitlementDataLoading?: boolean;
  isCurrentEntitlementFree: boolean | undefined;
  isDisplayEndNoCostPremiumBanner: (value: boolean) => void;
  isUpgradeTrialScenario: boolean;
  isExistingAnnualPlanForJCA?: boolean;
  isMonthlyDisabled?: boolean;
}

export const InlineMessage = ({ dismissMessage }: { dismissMessage: () => void }) => {
  return (
    <div style={{ position: 'relative' }}>
      <span className="inline-header">
        <InfoIcon label="Info" />
        <strong>View pricing details</strong>
      </span>
      <br />
      <label>For more information about this price plan, go to product pricing portal.</label>
      <br />
      <Button testId="pricing-details" style={{ paddingLeft: '0' }} appearance="link">
        View pricing details
      </Button>
      <span className="inline-dismiss-icon" onClick={dismissMessage}>
        <CrossIcon label="Close" />
      </span>
    </div>
  );
};

export const ManagePlan = ({
  entitlement,
  availableOfferings,
  availablePricingPlans,
  formState,
  transactionAccountContext,
  isSkipTrialChecked,
  isSkipTrialDisabled,
  onSkipTrialChange,
  onSkipTrialDisableChange,
  productSkipTrialEligible,
  setProductSkipTrialEligible,
  onIsImmediateFlagChange,
  evaluatePricingPlan,
  onSelectOfferingId,
  selectedOfferingId,
  linkedEntsTableVisible,
  linkedEntitlementsData,
  linkedEntitlementsLoading,
  linkedEntitlementsError,
  parentContainerID,
  productType,
  setBillingFrequency,
  setUserTier,
  billingFrequency,
  isBillingCycleChanged,
  defaultEntitlementData,
  isDefaultEntitlementDataLoading,
  isCurrentEntitlementFree,
  isDisplayEndNoCostPremiumBanner,
  isUpgradeTrialScenario,
  isExistingAnnualPlanForJCA,
  isMonthlyDisabled,
}: Props) => {
  const [disableUnits, setDisableUnits] = React.useState<boolean>(true);
  const [plans, setPlans] = useState<ValueType<OfferingSelectItem>>(undefined);
  const [selectedPricingPlan, setSelectedPricingPlan] = useState<PricingPlan>();
  const shouldResetPricingPlan = useRef(false);
  const initialBillingFrequency = entitlement?.pricingPlan?.billingFrequency;

  const [isOngoingUpgradeTrial, setIsOngoingUpgradeTrial] = useState<boolean>(false);
  const [isEnabled] = useFeatureToggle();
  const isSwitchToAnnualEnabled = isStatuspage(entitlement.productId)
    ? !entitlement.edition?.toLowerCase().includes('trial') || !entitlement.edition?.toLowerCase().includes('free')
    : isEnabled(ENABLE_JCA_SWITCH_TO_ANNUAL);

  const isViewPricingDetailsEnabled = isEnabled(ENABLE_VIEW_PRICING_DETAILS_ON_MANAGE);
  const isChangePricingEnabled = isEnabled(ENABLE_PRICING_PLAN_CHANGE);
  const [defaultPricingPlan, setDefaultPricingPlan] = React.useState<string>('');

  const manageUnitField = () => {
    if (
      defaultTo(formState?.newPlan?.label, defaultTo(entitlement?.offering?.name, ''))
        .toLowerCase()
        .includes(OfferingName.AUDIENCE_SPECIFIC.toLowerCase())
    ) {
      setDisableUnits(false);
    } else {
      setDisableUnits(true);
    }
  };

  useEffect(() => {
    setIsOngoingUpgradeTrial(!!entitlement.upgradeTrial);
  }, []);

  React.useEffect(() => {
    manageUnitField();
    setDefaultPricingPlanDetails(entitlement?.offering?.availableSyntheticPricingPlans ?? []);
  }, []);

  const availableOfferingOptions = defaultTo(availableOfferings, []).map((offering: Offering) => ({
    label: offering.name,
    value: offering.id,
  })) as OfferingSelectItem[];

  const onSelectOffering = (selectedOffering: ValueType<OfferingSelectItem, false>) => {
    onSelectOfferingId && onSelectOfferingId(selectedOffering?.value);
    if (defaultTo(selectedOffering?.label, '').toLowerCase().includes(OfferingName.AUDIENCE_SPECIFIC.toLowerCase())) {
      setDisableUnits(false);
    } else {
      setDisableUnits(true);
    }
    const availableSyntheticPricingPlans =
      availableOfferings?.find((offering: Offering) => offering?.id === selectedOffering?.value)
        ?.availableSyntheticPricingPlans ?? [];
    setDefaultPricingPlanDetails(availableSyntheticPricingPlans);
  };

  const { data, getTransactionAccount } = transactionAccountContext;

  const transactionAccount = getTransactionAccount(data);

  const getDefaultBillingGroup = (invoiceGroupId: string) => {
    return availableInvoiceGroups?.filter((invoiceGroup: InvoiceGroup) => {
      return invoiceGroup.id === invoiceGroupId;
    });
  };

  const [userTierOptions, defaultUserTier] = React.useMemo(() => {
    if (isStatuspage(entitlement?.productId) || billingFrequency !== BillingFrequency.ANNUAL) {
      return [[], null];
    }
    const options = getUserTierOptions({
      chargeQuantity: defaultEntitlementData?.chargeQuantities?.[0]?.quantity ?? 0,
      activeUsers: entitlement?.activeUsers ?? 0,
      userTiers: selectedPricingPlan?.tiers || [],
      isBulkEditEnabled: false,
    }) as UserTierSelectItem[];
    setUserTier(options[0]?.value || '');

    return [options, options[0]];
  }, [selectedPricingPlan, entitlement, isDefaultEntitlementDataLoading]);

  const scheduledChanges = entitlement?.scheduledChanges ?? null;
  const availableInvoiceGroups = (transactionAccount?.availableInvoiceGroups?.nodes ?? []) as InvoiceGroup[];

  const defaultBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;

  const hasSingleInvoiceGroup = availableInvoiceGroups?.length === 1;

  const scheduledBillingInvoiceGroupId = scheduledChanges?.scheduledBillingInvoiceGroupId;

  const defaultInvoiceGroupPricingPlan = scheduledBillingInvoiceGroupId
    ? getDefaultBillingGroup(scheduledBillingInvoiceGroupId)?.[0]?.shipToPriceEligibility
    : defaultBillingGroupId
    ? getDefaultBillingGroup(defaultBillingGroupId)?.[0]?.shipToPriceEligibility
    : hasSingleInvoiceGroup
    ? availableInvoiceGroups?.[0]?.shipToPriceEligibility
    : undefined;

  const setDefaultPricingPlanDetails = (availableSyntheticPricingPlans: Maybe<Maybe<SyntheticPricingPlan>[]>) => {
    let pricingPlan;

    const availablePricingPlan =
      availableSyntheticPricingPlans?.map((pricingPlan: Maybe<SyntheticPricingPlan>) => pricingPlan?.type) ?? [];

    const txAccountPricingPlan = (transactionAccount?.billToPartyPricingEligibilty ?? '').toUpperCase();

    const invoiceGroupPricingPlan = (defaultInvoiceGroupPricingPlan ?? '')?.toUpperCase();

    if (
      invoiceGroupPricingPlan &&
      availablePricingPlan.includes(invoiceGroupPricingPlan) &&
      invoiceGroupPricingPlan !== PricingPlanType.COMMERCIAL
    ) {
      pricingPlan = invoiceGroupPricingPlan;
    } else if (
      txAccountPricingPlan &&
      availablePricingPlan.includes(txAccountPricingPlan) &&
      txAccountPricingPlan !== PricingPlanType.COMMERCIAL
    ) {
      pricingPlan = txAccountPricingPlan;
    } else {
      pricingPlan = PricingPlanType.COMMERCIAL;
    }
    setDefaultPricingPlan(pricingPlan);
    evaluatePricingPlan && evaluatePricingPlan(pricingPlan);
  };

  const evaluateOffering = (selectedOffering?: Offering, currentOffering?: Offering) => {
    const isStatuspage = entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
    const isTrial = isTrialEntitlement(entitlement, isStatuspage);

    if (isStatuspage) {
      setProductSkipTrialEligible(false);
      return;
    }

    if (isBillingCycleChanged && !isCurrentEntitlementFree) {
      onSkipTrialChange(true);
      onSkipTrialDisableChange(true);
      return;
    }

    if (isOngoingUpgradeTrial) {
      isDisplayEndNoCostPremiumBanner(false);
    }

    if (selectedOffering && selectedOffering?.pricingType === PricingType.FREE) {
      setProductSkipTrialEligible(true);
      onSkipTrialChange(true);
      onSkipTrialDisableChange(true);
      return;
    }

    if (!currentOffering?.level || !selectedOffering?.level) return;

    if (currentOffering?.level === selectedOffering?.level) {
      if (!isTrial) {
        setProductSkipTrialEligible(false);
        onSkipTrialChange(false);
        onSkipTrialDisableChange(true);
      } else {
        setProductSkipTrialEligible(true);
        onSkipTrialChange(false);
        onSkipTrialDisableChange(false);
      }
    } else if (currentOffering?.level < selectedOffering?.level) {
      setProductSkipTrialEligible(true);
      // plan Upgrade - not accounting any user/qty change
      onSkipTrialChange(false);
      onSkipTrialDisableChange(false);
      if (selectedOffering?.name.toUpperCase() === EntitlementEdition.PREMIUM && isOngoingUpgradeTrial) {
        setProductSkipTrialEligible(true);
        onSkipTrialChange(true);
        onSkipTrialDisableChange(true);
        isDisplayEndNoCostPremiumBanner(true);
      }
    } else {
      setProductSkipTrialEligible(true);
      // plan downgrade - not accounting any user/qty change
      if (isTrial) {
        onSkipTrialChange(false);
        onSkipTrialDisableChange(false);
      } else {
        onSkipTrialChange(true);
        onSkipTrialDisableChange(true);
      }
    }
  };

  const evaluateSkipTrialEligibilty = (selectedId: string | undefined) => {
    const selectedOffering = availableOfferings?.find((offering: Offering) => offering?.id === selectedId);
    const offering = entitlement?.offering as Offering;
    evaluateOffering(defaultTo(selectedOffering, undefined), offering);
  };

  const isParentSenAvailable = () => !!entitlement?.parentSen;

  // Return unit count as '1' for FREE SP entitlements , since UTS data cant be relied on in this particular case .
  const statusPageUnitCount = (): string => {
    if (entitlement.offering?.pricingType === PricingType.FREE) {
      return '1';
    } else {
      return defaultTo(entitlement?.unitCount, '1');
    }
  };

  const getIsDisabledMonthly = () => {
    if (isStatuspage(entitlement?.productId)) return false;
    if (isMonthlyDisabled) return true;
    if (initialBillingFrequency === BillingFrequency.ANNUAL) return true;
  };
  return (
    <div className="plan-view">
      <h2>Select plan</h2>
      <div className="current-plans-container">
        <Field name="currentPlan" id="currentPlanName" label="Current plan" defaultValue={entitlement?.offering?.name}>
          {() => <p>{entitlement?.offering?.name}</p>}
        </Field>
        {(isBillingCycleChanged || isExistingAnnualPlanForJCA) && (
          <Field
            data-testid="currentUser"
            name="currentUser"
            id="currentUsersCount"
            label="Current users"
            defaultValue={entitlement?.activeUsers}
          >
            {() => <p>{isNil(entitlement?.activeUsers) ? '-' : entitlement?.activeUsers}</p>}
          </Field>
        )}
        {isExistingAnnualPlanForJCA && (
          <Field name="currentTier" id="currentTier" label={'Current user tier'}>
            {() => (
              <p>{!isNil(getUserTier(entitlement?.subscription)) ? getUserTier(entitlement?.subscription) : '-'}</p>
            )}
          </Field>
        )}
      </div>
      {
        <Field
          name="billingFrequency"
          label={
            <div>
              Billing cycle
              <Tooltip content="Free plans aren’t eligible for annual billing" tag={'span'}>
                <InfoIcon size="small" testId="info-icon" label="Info" />
              </Tooltip>
            </div>
          }
          defaultValue={billingFrequency}
        >
          {() => (
            <div className="buttonGroup-container">
              <ButtonGroup>
                <Button
                  isDisabled={getIsDisabledMonthly()}
                  id="billFrequency-monthly"
                  testId="billFrequency-monthly"
                  appearance={billingFrequency === BillingFrequency.MONTHLY ? 'primary' : 'default'}
                  onClick={() => {
                    setBillingFrequency(BillingFrequency.MONTHLY);
                    shouldResetPricingPlan.current = true;
                  }}
                  className={classNames({ ['selected']: billingFrequency === BillingFrequency.MONTHLY })}
                  selected={billingFrequency === BillingFrequency.MONTHLY}
                >
                  Monthly
                </Button>
                <Button
                  isDisabled={!isSwitchToAnnualEnabled}
                  id="billFrequency-annual"
                  testId="billFrequency-annual"
                  appearance={billingFrequency === BillingFrequency.ANNUAL ? 'primary' : 'default'}
                  onClick={() => {
                    shouldResetPricingPlan.current = true;
                    setBillingFrequency(BillingFrequency.ANNUAL);
                  }}
                  className={classNames({ ['selected']: billingFrequency === BillingFrequency.ANNUAL })}
                  selected={billingFrequency === BillingFrequency.ANNUAL}
                >
                  Annual
                </Button>
              </ButtonGroup>
            </div>
          )}
        </Field>
      }

      {billingFrequency === BillingFrequency.ANNUAL && linkedEntsTableVisible && (
        <div className="linked-entitlements">
          {isBillingCycleChanged && (
            <InfoPanel
              type="warning"
              title={'Keep in mind'}
              message={
                'There are other subscriptions linked to this product family – billing cycle changes will apply to them as well.'
              }
            />
          )}
          <entitlementUtils.LinkedEntitlementsTable
            message=" "
            header="Linked subscriptions that this change will apply to"
            entitlementId={entitlement.id ?? ''}
            parentContainerID={parentContainerID}
            productType={productType}
            destinationEntitlementType={[
              LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
              LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
              LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
              LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
            ]}
            linkedEntitlementsData={linkedEntitlementsData}
            linkedEntitlementsLoading={linkedEntitlementsLoading}
            linkedEntitlementsError={linkedEntitlementsError}
          />
        </div>
      )}

      {!isParentSenAvailable() && (
        <Field<ValueType<OfferingSelectItem>, HTMLSelectElement>
          label="New plan"
          name="newPlan"
          defaultValue={defaultTo(plans, formState?.newPlan)}
        >
          {({ fieldProps }) => (
            <div className="plan-select-item" data-testid="new-plan-select">
              <Select<OfferingSelectItem>
                {...fieldProps}
                isSearchable={false}
                options={availableOfferingOptions}
                value={isBillingCycleChanged && !isCurrentEntitlementFree ? null : defaultTo(plans, formState?.newPlan)}
                className="plan-field"
                billingFrequency
                isDisabled={isExistingAnnualPlanForJCA || (isBillingCycleChanged && !isCurrentEntitlementFree)}
                initialBillingFrequency={initialBillingFrequency}
                onChange={(offering: ValueType<OfferingSelectItem, false>) => {
                  onSelectOffering(offering);
                  evaluateSkipTrialEligibilty(offering?.value);
                  shouldResetPricingPlan.current = true;
                  onIsImmediateFlagChange(false);
                  setPlans(offering);
                  if (!isStatuspage(entitlement?.productId)) {
                    if (
                      offering?.label?.toLowerCase().includes('free') &&
                      billingFrequency === BillingFrequency.ANNUAL
                    ) {
                      setBillingFrequency(BillingFrequency.MONTHLY);
                    }
                  }
                  fieldProps.onChange(offering);
                }}
              />
            </div>
          )}
        </Field>
      )}

      {productSkipTrialEligible && (
        <Field label="Skip trial" name="skipTrial">
          {({ fieldProps }) => (
            <Checkbox
              {...fieldProps}
              defaultChecked={isSkipTrialChecked}
              isChecked={isSkipTrialChecked}
              isDisabled={isSkipTrialDisabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onSkipTrialChange(event.currentTarget.checked);
                fieldProps.onChange(event);
              }}
              testId="skipTrial"
            />
          )}
        </Field>
      )}

      {isUpgradeTrialScenario &&
        entitlement?.offering?.name.toUpperCase() === EntitlementEdition.STANDARD &&
        selectedOfferingId && (
          <span data-testid="upgrade-trial-banner" className="upgrade-trial-banner">
            <WarningIcon label="" primaryColor={Y200} />
            <p>Current plan will be charged during the no-cost trial period of Premium plan</p>
          </span>
        )}

      {isStatuspage(entitlement?.productId) && (
        <Field
          label={'Units'}
          name="units"
          isDisabled={disableUnits}
          defaultValue={defaultTo(formState?.units, statusPageUnitCount())}
          isRequired={true}
          validate={(value: any) =>
            value && value.length > 0 && isInteger(+value) && +value >= 0 ? undefined : 'INVALID'
          }
        >
          {({ fieldProps, error }) => (
            <>
              <Textfield
                className="unit-field"
                {...fieldProps}
                name={'units'}
                testId="units-field"
                data-testid="units-field"
              />
              {error && <ErrorMessage>Enter a valid number</ErrorMessage>}
            </>
          )}
        </Field>
      )}

      {billingFrequency === BillingFrequency.ANNUAL && !isStatuspage(entitlement?.productId) && (
        <Field<ValueType<UserTierSelectItem>, HTMLSelectElement>
          label="User tier"
          name="userTier"
          id="userTier"
          defaultValue={isDefaultEntitlementDataLoading ? null : defaultTo(formState?.userTier, defaultUserTier)}
          data-testid="userTier"
        >
          {({ fieldProps }) => (
            <Select<UserTierSelectItem>
              {...fieldProps}
              isSearchable={false}
              isDisabled={!isCurrentEntitlementFree}
              options={userTierOptions}
              isLoading={isDefaultEntitlementDataLoading}
              placeholder="Select tier..."
              className="plan-field"
              onChange={(e: ValueType<UserTierSelectItem, false>) => {
                setUserTier && setUserTier(e?.value);
                fieldProps.onChange(e);
              }}
            />
          )}
        </Field>
      )}

      {isChangePricingEnabled ? (
        <ChangePricePlan
          entitlement={entitlement}
          isManagePane={true}
          formState={formState}
          availablePricingPlans={availablePricingPlans}
          planId={formState?.pricePlan?.value ?? entitlement?.pricingPlan?.id}
          selectedOfferingId={selectedOfferingId}
          billingFrequency={billingFrequency}
          setSelectedPricingPlan={setSelectedPricingPlan}
          shouldResetPricingPlan={shouldResetPricingPlan}
        />
      ) : (
        <Field
          name="pricing"
          id="pricing"
          label="Pricing"
          defaultValue={defaultTo(defaultPricingPlan, 'not applicable')}
        >
          {() => (
            <div className="pricing-inline">
              <span>{capitalize(defaultTo(defaultPricingPlan, 'not applicable'))}</span>
              {isViewPricingDetailsEnabled && (
                <DismissableInlineMessage
                  position="right"
                  renderMessage={(dismissMessage) => <InlineMessage dismissMessage={dismissMessage} />}
                  renderTitle={(toggleMessage) => (
                    <span className="info-icon" onClick={toggleMessage}>
                      <InfoIcon testId="info-icon" label="Info" />
                    </span>
                  )}
                />
              )}
            </div>
          )}
        </Field>
      )}
    </div>
  );
};
